import React, { useState } from "react";
import { Check } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { pick } from "@/lib/languages";
import { homeL } from "@/i18n/home";
import { Field } from "@/components/ds/Field";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * The newsletter: one quiet row at the end of Home. A labelled email field,
 * an ink button that keeps its width while the request runs, and the result
 * announced once (role=status). Nothing here is red: subscribing is not the
 * screen's commitment.
 */
export default function Newsletter() {
  const { lang, siteLang = lang } = useSettings();
  const l = pick(homeL, siteLang).newsletter;
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");

  const subscribe = async (event) => {
    event.preventDefault();
    if (!email.trim() || state === "busy" || state === "done") return;
    setState("busy");
    try {
      await api.post("/newsletter/subscribe", { email: email.trim(), lang });
      setState("done");
    } catch {
      setState("failed");
    }
  };

  return (
    <section aria-labelledby="home-newsletter-title" data-testid="home-newsletter" className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid gap-5 border-t border-touki pt-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,28rem)] lg:items-end lg:gap-10 lg:pt-10">
        <div className="min-w-0">
          <h2 id="home-newsletter-title" className="text-heading-lg text-touki-black">{l.title}</h2>
          <p className="mt-1 text-body-sm text-touki-muted text-pretty">{l.sub}</p>
        </div>
        <form onSubmit={subscribe} data-state={state} className="flex flex-col gap-2">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <Field label={l.label} className="flex-1">
              <Input type="email" name="email" size="lg" required autoComplete="email" inputMode="email" spellCheck={false} placeholder={l.placeholder}
                value={email} onChange={(e) => { setEmail(e.target.value); if (state !== "busy") setState("idle"); }}
                disabled={state === "done"} data-testid="newsletter-email" />
            </Field>
            <Button type="submit" variant="ink" size="md" loading={state === "busy"} disabled={state === "done"}
              className="sm:min-w-[9rem]" data-testid="newsletter-submit">
              {state === "done" ? <><Check weight="bold" aria-hidden="true" />{l.cta}</> : l.cta}
            </Button>
          </div>
          <p role="status" aria-live="polite" data-testid="newsletter-status"
            className={`text-label-md empty:hidden ${state === "failed" ? "text-touki-red" : "text-touki-success"}`}>
            {state === "done" ? l.done : state === "failed" ? l.failed : ""}
          </p>
        </form>
      </div>
    </section>
  );
}
